import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { ConnectionItemShape, ConnectionNetworkShape } from '../../helpers/propTypeShapes';
import { selectSortedWorkflows } from '../../store/connectionManager';
import formForNetwork from './formForNetwork';

import './ConnectionManagerModal.css';

const customModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    minWidth: '610px',
    padding: '40px 24px 30px',
    textAlign: 'center',
    transform: 'translate(-50%, -50%)',
  },
  overlay: {
    backgroundColor: 'rgb(0,0,0,0.5)',
    zIndex: '3',
  },
};

const Network = ({ network, onSelect }) => {
  const { logo, title, isSupported = false } = network;
  const tooltipText = !isSupported ? 'Coming soon' : '';

  const handleOnSelect = () => {
    if (isSupported) {
      onSelect(network);
    }
  };

  const handleOnSelectByKeyword = (e) => {
    if (isSupported && e.charCode === 13) {
      onSelect(network);
    }
  };

  return (
    <div
      className="network"
      data-tip={tooltipText}
      onClick={handleOnSelect}
      onKeyDown={handleOnSelectByKeyword}
      role="button"
      tabIndex="0"
    >
      <div className={classnames('network-content', { disabled: !isSupported })}>
        <img className="network-logo" src={logo} alt={`${title} logo`} />
        <p>{title}</p>
      </div>
      <ReactTooltip effect="solid" type="light" border offset={{ bottom: 60 }} />
    </div>
  );
};

Network.propTypes = {
  network: ConnectionNetworkShape.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const ConnectionManagerModal = ({
  isOpen,
  isEditTab,
  modalTitle,
  headerIcon,
  handleOnClose,
  connection,
  handleOnChannelSelected,
  handleOnConnectionFormChange,
  handleOnSave,
  showBreadcrumbNav,
}) => {
  const solutions = useSelector(selectSortedWorkflows);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    Modal.setAppElement('body');
  }, []);

  const FormComponent = formForNetwork(connection?.network);

  return (
    <Modal isOpen={isOpen} onRequestClose={() => handleOnClose()} style={customModalStyles}>
      <div className="connections-modal-container">
        <div className="connections-modal-header">
          {headerIcon}
          <h3 className="title">{modalTitle}</h3>
        </div>

        {showBreadcrumbNav && (
          <div className="connections-modal-tab-panel">
            <span className={classnames('tab-item', { active: !isEditTab })}>1. SELECT</span>
            <span className={classnames('tab-item', { active: isEditTab })}>2. CONFIGURE</span>
          </div>
        )}

        <div className="connections-modal-content">
          {!isEditTab && (
            <div className="panel">
              <div className="networks-container">
                <div className="networks-group">
                  {solutions.map((network) => (
                    <Network key={network.id} network={network} onSelect={handleOnChannelSelected} />
                  ))}
                </div>
              </div>
              <div className="action-container">
                <button className="btn btn-close" type="button" onClick={handleOnClose}>
                  cancel
                </button>
              </div>
            </div>
          )}

          {isEditTab && connection?.network && (
            <div className="panel">
              <div className="edit-form-container">
                <div className="network-container">
                  <div className="network-info">
                    <img
                      className="network-logo"
                      src={connection.network.logo}
                      alt={`${connection.network.title} logo`}
                    />
                    <p>{connection.network.title}</p>
                  </div>
                  <i className="icon icon-checked-circle" />
                </div>

                <FormComponent
                  setIsFormValid={setIsFormValid}
                  handleOnConnectionFormChange={handleOnConnectionFormChange}
                  connection={connection}
                />
              </div>
              <div className="action-container">
                <button className="btn btn-close" type="button" onClick={handleOnClose}>
                  Cancel
                </button>
                <button className="btn" type="button" disabled={!isFormValid} onClick={handleOnSave}>
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

ConnectionManagerModal.defaultProps = {
  connection: null,
  showBreadcrumbNav: true,
};

ConnectionManagerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isEditTab: PropTypes.bool.isRequired,
  connection: ConnectionItemShape,
  modalTitle: PropTypes.string.isRequired,
  headerIcon: PropTypes.node.isRequired,
  handleOnClose: PropTypes.func.isRequired,
  handleOnChannelSelected: PropTypes.func.isRequired,
  handleOnConnectionFormChange: PropTypes.func.isRequired,
  handleOnSave: PropTypes.func.isRequired,
  showBreadcrumbNav: PropTypes.bool,
};

export default ConnectionManagerModal;
